import { Accordion, Alert, Badge, Checkbox, Code, Group, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import { isVerificationSelectionChanged } from '@/services/verificationErrors.ts';
import {
  verificationGroups,
  type VerificationChangeReceipt,
  type VerificationChangeRequest,
  type VerificationInventory,
  type VerificationPlan,
  type VerificationProfileContext,
  type VerificationPreparation,
  type VerificationPreparationResult,
  type VerificationSelection,
} from '@/types/verification.ts';

type Props = {
  automationId: string;
  versionId: string;
  preparationInFlight: boolean;
  onPrepareCase: (caseId: string, context: VerificationPreparation) => Promise<VerificationPreparationResult>;
  onLibraryChanged: () => void;
};

const operationLabels = {
  promote_diagnostic: 'Keep diagnostic as optional regression',
  retire_case: 'Retire saved case',
  retire_fixture: 'Retire recorded inputs',
};

function readReceipt(key: string): VerificationChangeReceipt | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null') as VerificationChangeReceipt | null;
    return value?.request?.request_id && value.preview?.preview_digest
      && ['reviewed', 'pending', 'committed'].includes(value.state) ? value : null;
  } catch { return null; }
}

function definitivelyRejected(error: unknown) {
  return axios.isAxiosError(error) && [400, 403, 404, 409, 422].includes(error.response?.status ?? 0);
}

function profileDraft(context: VerificationProfileContext) {
  return JSON.stringify(context.profile?.snapshot ?? {
    schema_version: 'automation_verification_profile.v1',
    suite_revision_id: context.selection.suite.selected_revision_id,
    package_revision_id: context.selection.package.selected_revision_id,
    coverage: [],
  }, null, 2);
}

/** Presents server advice. Execution stays with the existing sealed-plan controls. */
const VerificationPanel = ({ automationId, versionId, preparationInFlight, onPrepareCase, onLibraryChanged }: Props) => {
  const api = useApiClient();
  const { get } = api;
  const base = `/automations/${automationId}/automation-versions/${versionId}/verification`;
  const storageKey = `verification-change:${automationId}:${versionId}`;
  const [plan, setPlan] = useState<VerificationPlan | null>(null);
  const planRequest = useRef<Record<string, unknown> | null>(null);
  const [context, setContext] = useState<VerificationProfileContext | null>(null);
  const [inventory, setInventory] = useState<VerificationInventory | null>(null);
  const [comparison, setComparison] = useState('');
  const [purpose, setPurpose] = useState<string | null>('diagnostic');
  const [caseIds, setCaseIds] = useState('');
  const [boundaryIds, setBoundaryIds] = useState('');
  const [repeatSample, setRepeatSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState('');
  const [storageWarning, setStorageWarning] = useState('');
  const [message, setMessage] = useState('');
  const [profileJson, setProfileJson] = useState('');
  const profileDirty = useRef(false);
  // Read-only context refreshes must not silently rebase an unsaved coverage edit.
  const profileSelection = useRef<VerificationSelection | null>(null);
  const contextRead = useRef(0);
  const contextInvalidated = useRef(false);
  const [changeNote, setChangeNote] = useState('');
  const [operation, setOperation] = useState<VerificationChangeRequest['operation']>('promote_diagnostic');
  const [caseId, setCaseId] = useState('');
  const [sourceRunId, setSourceRunId] = useState('');
  const [fixtureId, setFixtureId] = useState('');
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState(() => readReceipt(storageKey));
  const [profilePending, setProfilePending] = useState(() => {
    try { return sessionStorage.getItem(`${storageKey}:profile`); } catch { return null; }
  });

  const persistSession = (key: string, value: string | null, required = false) => {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
      setStorageWarning('');
      return true;
    } catch {
      setStorageWarning('Browser session storage is unavailable. Keep this tab open; recovery state may be incomplete. New changes require working session storage.');
      if (required) throw new Error('Change not submitted: its recovery request could not be saved in browser session storage.');
      return false;
    }
  };
  const persistReceipt = (next: VerificationChangeReceipt | null, required = false) => {
    // A mutation is submitted only after its exact retry identity is durable.
    if (!persistSession(storageKey, next ? JSON.stringify(next) : null, required) && next === null) return;
    setReceipt(next);
  };
  const invalidateRecommendations = () => {
    contextInvalidated.current = true;
    setPlan(null);
    setInventory(null);
    planRequest.current = null;
  };
  const perform = async (work: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try { await work(); }
    catch (failure) {
      if (isVerificationSelectionChanged(failure)) invalidateRecommendations();
      setError(getHttpErrorMessage(failure) || 'The request could not be confirmed. Refresh or retry the same retained request.');
    }
    finally { inFlight.current = false; setBusy(false); }
  };
  const loadContext = async (resetDraft = false) => {
    const generation = ++contextRead.current;
    const response = await api.get<VerificationProfileContext>(`${base}/profile`);
    if (generation !== contextRead.current) return response.data;
    contextInvalidated.current = false;
    setContext(response.data);
    if (resetDraft || !profileSelection.current) profileSelection.current = response.data.selection;
    if (resetDraft) {
      profileDirty.current = false;
      setProfileJson(profileDraft(response.data));
    }
    return response.data;
  };
  useEffect(() => {
    // The parent keys this panel by version; responses cannot cross versions.
    let active = true;
    const generation = ++contextRead.current;
    get<VerificationProfileContext>(`${base}/profile`).then(({ data }) => {
      if (!active || generation !== contextRead.current) return;
      setContext(data);
      if (!profileSelection.current) profileSelection.current = data.selection;
      if (!profileDirty.current) {
        profileSelection.current = data.selection;
        setProfileJson(profileDraft(data));
      }
    }).catch((failure) => { if (active && generation === contextRead.current) setError(getHttpErrorMessage(failure) || 'Verification context could not be loaded.'); });
    return () => { active = false; };
  }, [get, base]);

  const getAdvice = (continuation?: string) => perform(async () => {
    setPlan(null);
    // The plan request fences this observation. Refresh only when needed,
    // rather than reading the same profile before every read-only request.
    const current = context && !contextInvalidated.current ? context : await loadContext();
    if (!current) return;
    const request = continuation && planRequest.current
      ? { ...planRequest.current, evidence_cursor: continuation }
      : {
        subject: { kind: 'version', automation_version_id: versionId },
        expected_selection: current.selection,
        purpose,
        ...(comparison.trim() ? { comparison_base_version_id: comparison.trim() } : {}),
        case_ids: caseIds.split(',').map((id) => id.trim()).filter(Boolean),
        boundary_ids: boundaryIds.split(',').map((id) => id.trim()).filter(Boolean),
        repeat_sample: repeatSample,
        max_native_cases: 5,
      };
    const { data } = await api.post<VerificationPlan>('/builder-verification/plan', request);
    planRequest.current = request;
    setPlan(data);
  });
  const getInventory = (cursor?: string) => perform(async () => {
    let data: VerificationInventory;
    try {
      ({ data } = await api.get<VerificationInventory>(`${base}/inventory`, { params: { limit: 20, ...(cursor ? { cursor } : {}) } }));
    } catch (failure) {
      if (definitivelyRejected(failure)) setInventory(null);
      throw failure;
    }
    setInventory((prior) => ({ ...data, checks: cursor && prior ? [...prior.checks, ...data.checks] : data.checks }));
  });
  const previewChange = () => perform(async () => {
    const current = await loadContext();
    const request: VerificationChangeRequest = {
      operation, automation_version_id: versionId, expected_selection: current.selection,
      reason: reason.trim(), request_id: crypto.randomUUID(),
      ...(operation !== 'retire_fixture' ? { case_id: caseId.trim() } : { fixture_id: fixtureId.trim() }),
      ...(operation === 'promote_diagnostic' ? { source_run_id: sourceRunId.trim() } : {}),
    };
    const { data } = await api.post<VerificationChangeReceipt['preview']>(`/automations/${automationId}/verification/lifecycle/preview`, request);
    persistReceipt({ request: { ...request, preview_digest: data.preview_digest }, preview: data, state: 'reviewed' });
  });
  const applyChange = () => perform(async () => {
    if (!receipt || !receipt.preview.allowed || receipt.state === 'committed') return;
    persistReceipt({ ...receipt, state: 'pending' }, true);
    try {
      await api.post(`/automations/${automationId}/verification/lifecycle/apply`, receipt.request);
    } catch (failure) {
      if (definitivelyRejected(failure)) persistReceipt({ ...receipt, state: 'reviewed' });
      throw failure;
    }
    persistReceipt({ ...receipt, state: 'committed' });
    setPlan(null);
    setInventory(null);
    onLibraryChanged();
    setMessage('Library change saved. Existing run evidence is retained; refresh recommendations for the new selection.');
    try { await loadContext(); }
    catch { setContext(null); throw new Error('Library change was saved, but context refresh failed. Reload verification context before further changes.'); }
  });
  const saveProfile = () => perform(async () => {
    // Retain the exact CAS request across an ambiguous response or remount.
    const key = `${storageKey}:profile`;
    let existing: string | null;
    try { existing = sessionStorage.getItem(key); }
    catch { throw new Error('Change not submitted: browser session storage is unavailable.'); }
    if (!existing && (!context || !profileSelection.current)) return;
    const request = existing ? JSON.parse(existing) : {
      expected_selection: profileSelection.current, snapshot: JSON.parse(profileJson), change_note: changeNote.trim(), request_id: crypto.randomUUID(),
    };
    persistSession(key, JSON.stringify(request), true);
    setProfilePending(JSON.stringify(request));
    try {
      await api.put(`${base}/profile`, request);
    } catch (failure) {
      if (definitivelyRejected(failure)) {
        if (persistSession(key, null)) setProfilePending(null);
      }
      throw failure;
    }
    if (persistSession(key, null)) setProfilePending(null);
    setPlan(null);
    setInventory(null);
    onLibraryChanged();
    setMessage('Coverage profile saved. Refresh recommendations to use the new selection.');
    try { await loadContext(true); }
    catch { setContext(null); throw new Error('Coverage profile was saved, but context refresh failed. Reload verification context before further changes.'); }
  });

  return <Stack gap="sm" aria-label="Shared verification" style={{ minWidth: 0 }}>
    <Group justify="space-between"><Text fw={600}>Verification recommendations</Text><Badge color="gray">{context?.status ?? 'Loading'} profile</Badge></Group>
    <Text size="sm" c="dimmed">Choose checks for this change. Recommendations do not execute tests or grant acceptance.</Text>
    {error && <Alert color="red" title="Verification needs attention">{error}</Alert>}
    {storageWarning && <Alert color="yellow" title="Recovery storage needs attention">{storageWarning}</Alert>}
    {message && <Alert color="green">{message}</Alert>}
    <Group align="end">
      <TextInput label="Compare with version (optional)" disabled={busy} value={comparison} onChange={(event) => { setComparison(event.currentTarget.value); setPlan(null); }} style={{ flex: 1, minWidth: 180 }} />
      <Select label="Verification purpose" disabled={busy} value={purpose} data={[{ value: 'diagnostic', label: 'Development' }, { value: 'acceptance', label: 'Acceptance' }]} onChange={(value) => { setPurpose(value); setPlan(null); }} allowDeselect={false} />
      <StyledButton variant="outline" disabled={busy || !context} onClick={() => void getAdvice()}>Review recommended checks</StyledButton>
    </Group>
    <Accordion><Accordion.Item value="scope"><Accordion.Control>Narrow the check selection</Accordion.Control>
      <Accordion.Panel><Stack gap="sm">
        <Text size="sm">Leave IDs blank for backend recommendations. Explicit IDs select the questions to assess; they do not waive required acceptance.</Text>
        <TextInput label="Case IDs (comma-separated)" disabled={busy} value={caseIds} onChange={(event) => { setCaseIds(event.currentTarget.value); setPlan(null); }} />
        <TextInput label="Local boundary IDs (comma-separated)" disabled={busy} value={boundaryIds} onChange={(event) => { setBoundaryIds(event.currentTarget.value); setPlan(null); }} />
        <Checkbox label="Request a new independent sample" disabled={busy} checked={repeatSample} onChange={(event) => { setRepeatSample(event.currentTarget.checked); setPlan(null); }} />
      </Stack></Accordion.Panel>
    </Accordion.Item></Accordion>
    {plan && <Stack gap="xs">
      <Text size="sm" style={{ overflowWrap: 'anywhere' }}>Plan: {plan.status} · <Code style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{plan.digest}</Code></Text>
      <Text size="xs">Observation completeness does not mean checks or acceptance have passed.</Text>
      {plan.profile_status && <Text size="sm">Plan coverage profile: {plan.profile_status}</Text>}
      {!!plan.changed_step_ids?.length && <Text size="sm" style={{ overflowWrap: 'anywhere' }}>Changed steps: {plan.changed_step_ids.join(', ')}</Text>}
      {!!plan.change_categories?.length && <Text size="sm">Change categories: {plan.change_categories.join(', ')}</Text>}
      {!plan.complete && <Alert color="yellow" title="Evidence search incomplete">Results remain provisional. Unresolved checks: {plan.unresolved_check_ids.join(', ') || 'see recommendations'}. Select omitted check IDs for a focused review.
        {plan.continuation && <StyledButton variant="subtle" disabled={busy} onClick={() => void getAdvice(plan.continuation!)}>Continue evidence search</StyledButton>}
      </Alert>}
      {plan.warnings.map((warning, i) => <Text key={i} size="sm">{warning}</Text>)}
      <Accordion multiple>
        {verificationGroups.map(([key, label]) => <Accordion.Item key={key} value={key}>
          <Accordion.Control>{label} ({plan[key].length})</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">{plan[key].map((action) => <Stack key={action.id} gap={4} style={{ overflowWrap: 'anywhere' }}>
            <Text fw={500} size="sm">{action.check_id ?? action.id} · {action.proof_scope} · {action.eligibility}</Text>
            <Text size="sm">{action.explanation}</Text>
            <Text size="xs" c="dimmed">{action.reason_code} · {action.proof_surface} proof</Text>
            <Text size="xs">Coverage basis: {action.coverage_basis ?? 'unknown'}</Text>
            {!!action.target_step_ids?.length && <Text size="xs">Targets: {action.target_step_ids.join(', ')}</Text>}
            {!!action.requirement_refs?.length && <Text size="xs">Requirements: {action.requirement_refs.join(', ')}</Text>}
            <Text size="xs">Duration: {action.cost.status}{action.cost.sample_count ? ` · ${action.cost.sample_count} observation${action.cost.sample_count === 1 ? '' : 's'}` : ''}. {action.cost.note}</Text>
            {action.proof_surface === 'local' && action.cost.observed_wall_seconds.length > 0
              && <Text size="xs">Local elapsed time: {action.cost.observed_wall_seconds.join(', ')} seconds.</Text>}
            {(action.cost.observed_cumulative_execution_seconds?.length ?? 0) > 0
              && <Text size="xs">Accumulated execution time: {action.cost.observed_cumulative_execution_seconds!.join(', ')} seconds. Excludes time between executions.</Text>}
            {!(action.proof_surface === 'local' && action.cost.observed_wall_seconds.length > 0)
              && !action.cost.observed_cumulative_execution_seconds?.length
              && <Text size="xs">No measured duration available.</Text>}
            {action.limitations.map((limitation, i) => <Text key={i} size="xs">{limitation}</Text>)}
            {action.evidence?.map((evidence) => <Text key={`${evidence.owner}:${evidence.reference_id}`} size="xs">Evidence: {evidence.reference_id} · {evidence.owner} · {evidence.status}</Text>)}
            {action.footprint && <Text size="xs">Scheduled: {action.footprint.scheduled_step_ids.join(', ') || 'none'} · Fresh: {action.footprint.fresh_step_ids.join(', ') || 'none'} · Asserted: {action.footprint.asserted_step_ids.join(', ') || 'none'}</Text>}
            {action.next_operation && <Text size="xs">Next operation: {action.next_operation}</Text>}
            {key === 'run_now' && action.kind === 'native_case' && action.eligibility === 'eligible' && action.check_id
              && <StyledButton variant="subtle" disabled={busy || preparationInFlight} onClick={() => void perform(async () => {
                const result = await onPrepareCase(action.check_id!, {
                  expected_selection: plan.selection,
                  purpose: purpose === 'acceptance' ? 'acceptance' : 'diagnostic',
                  ...(comparison.trim() ? { comparison_base_version_id: comparison.trim() } : {}),
                });
                if (result.status === 'selection_stale') {
                  invalidateRecommendations();
                  setError(result.message);
                }
              })}>Prepare {action.check_id}</StyledButton>}
          </Stack>)}</Stack></Accordion.Panel>
        </Accordion.Item>)}
      </Accordion>
    </Stack>}
    <Accordion>
      <Accordion.Item value="library"><Accordion.Control>Test library and coverage</Accordion.Control>
        <Accordion.Panel><Stack gap="sm">
          <StyledButton variant="subtle" disabled={busy} onClick={() => void getInventory()}>Inspect selected checks</StyledButton>
          {inventory && <>
            <Text size="sm">{inventory.checks.length} of {inventory.total} checks. {inventory.note}</Text>
            {!inventory.evidence_complete && <Text size="sm">Evidence search is incomplete; use recommendations to continue it.</Text>}
            {inventory.checks.map((check) => <Text key={`${check.kind}:${check.id}`} size="sm" style={{ overflowWrap: 'anywhere' }}>
              {check.id} · {check.kind} · {check.required ? 'required' : 'optional'} · {check.evidence.reason_code}
              {Boolean(check.exact_intent_duplicates?.length) && ` · Similar intent: ${check.exact_intent_duplicates!.join(', ')}`}
            </Text>)}
            {!inventory.complete && inventory.next_cursor && <StyledButton variant="subtle" disabled={busy} onClick={() => void getInventory(inventory.next_cursor!)}>Load more checks</StyledButton>}
          </>}
          <Textarea label="Coverage profile (JSON)" disabled={busy || Boolean(profilePending)} autosize minRows={6} maxRows={15} value={profileJson} onChange={(event) => { profileDirty.current = true; setProfileJson(event.currentTarget.value); }} />
          <StyledButton variant="subtle" disabled={busy || Boolean(profilePending)} onClick={() => void perform(async () => {
            await loadContext(true);
            setPlan(null);
            setInventory(null);
          })}>Reload saved coverage and context</StyledButton>
          <Text size="xs">Declare independent expectations, proof scope, applicability and exact check revisions. Missing or stale coverage remains unknown. A retained profile retry submits its original request.</Text>
          <TextInput label="Coverage change reason" disabled={busy || Boolean(profilePending)} value={changeNote} onChange={(event) => setChangeNote(event.currentTarget.value)} />
          {profilePending && <><Text size="sm">A retained save needs recovery or local cleanup. Retry its exact request before changing coverage.</Text><Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{profilePending}</Code></>}
          <StyledButton variant="outline" disabled={busy || receipt?.state === 'pending' || (!profilePending && (!context || !changeNote.trim()))} onClick={() => void saveProfile()}>{profilePending ? 'Retry same profile save' : 'Save coverage profile'}</StyledButton>
          {!receipt && <>
          <Select label="Library change" value={operation} data={Object.entries(operationLabels).map(([value, label]) => ({ value, label }))} onChange={(value) => setOperation(value as VerificationChangeRequest['operation'])} allowDeselect={false} disabled={busy || Boolean(profilePending)} />
          {operation !== 'retire_fixture' && <TextInput label="Case ID" value={caseId} onChange={(event) => setCaseId(event.currentTarget.value)} disabled={busy || Boolean(profilePending)} />}
          {operation === 'promote_diagnostic' && <TextInput label="Diagnostic source run ID" value={sourceRunId} onChange={(event) => setSourceRunId(event.currentTarget.value)} disabled={busy || Boolean(profilePending)} />}
          {operation === 'retire_fixture' && <TextInput label="Fixture ID" value={fixtureId} onChange={(event) => setFixtureId(event.currentTarget.value)} disabled={busy || Boolean(profilePending)} />}
          <TextInput label="Library change reason" value={reason} onChange={(event) => setReason(event.currentTarget.value)} disabled={busy || Boolean(profilePending)} />
          <StyledButton variant="outline" disabled={busy || !context || !reason.trim() || Boolean(profilePending)} onClick={() => void previewChange()}>Preview library change</StyledButton>
          </>}
          {receipt && <>
            <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{operationLabels[receipt.request.operation]} · {receipt.request.case_id ?? receipt.request.fixture_id} · {receipt.request.reason}</Text>
            {receipt.request.source_run_id && <Text size="sm">Source run: {receipt.request.source_run_id}</Text>}
            <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(receipt.preview, null, 2)}</Code>
            <Text size="sm">Request {receipt.request.request_id} · {receipt.state}</Text>
            {receipt.state !== 'committed' && <StyledButton variant="outline" disabled={busy || !receipt.preview.allowed || Boolean(profilePending)} onClick={() => void applyChange()}>
              {receipt.state === 'pending' ? 'Retry same library change' : 'Apply reviewed library change'}
            </StyledButton>}
            {receipt.state !== 'pending' && <StyledButton variant="subtle" disabled={busy} onClick={() => void perform(async () => { persistReceipt(null); })}>Clear reviewed change</StyledButton>}
          </>}
        </Stack></Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  </Stack>;
};

export default VerificationPanel;
